import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Sparkles } from 'lucide-react';
import type { AudienceMeta } from '@shared/constants/audiences';
import { Container } from '../layout/Container';
import { Reveal } from '../motion/Reveal';
import type { AudienceFaqItem, AudienceVoice } from '../../config/audience-voice';
import { getAudienceTheme } from '../../config/audience-theme';
import { FAQPageLd } from '../seo/jsonld/FAQPage';
import { cn } from '../../lib/cn';
import { easeStripe } from '../../lib/motion';

export interface SectorFAQProps {
  audience: AudienceMeta;
  voice: AudienceVoice;
  /** Override des items par défaut (sinon voice.faq.items) */
  items?: AudienceFaqItem[];
}

/**
 * SectorFAQ — FAQ moderne avec accordéons animés, ton adapté à l'audience.
 *
 * Design 2026 :
 *  - Cartes individuelles (vs liste divide-y) → meilleure lisibilité
 *  - Animations souples sur l'ouverture (max-height auto via framer)
 *  - Couleur d'accent par audience (chip ouvert)
 *  - "Vous ne trouvez pas votre réponse ?" → CTA contact en pied
 */
export function SectorFAQ({ audience, voice, items }: SectorFAQProps) {
  const theme = getAudienceTheme(audience.id);
  const data = items ?? voice.faq.items;
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  if (data.length === 0) return null;

  return (
    <section className="bg-stone-50/50 py-16 md:py-20">
      <FAQPageLd items={data.map((item) => ({ question: item.q, answer: item.a }))} />
      <Container className="max-w-3xl">
        <Reveal>
          <div className="text-center">
            <p className="eyebrow justify-center">{voice.faq.eyebrow}</p>
            <h2 className="mt-3 h-section">{voice.faq.title}</h2>
          </div>
        </Reveal>

        <div className="mt-10 space-y-2.5">
          {data.map((item, idx) => {
            const isOpen = idx === openIdx;
            return (
              <motion.article
                key={item.q}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.35, delay: idx * 0.04, ease: easeStripe }}
                className={cn(
                  'group relative overflow-hidden rounded-xl border bg-paper transition-all duration-200 ease-out',
                  isOpen
                    ? 'border-stone-300 shadow-elevated'
                    : 'border-stone-200/70 hover:border-stone-300 hover:shadow-soft',
                )}
              >
                <button
                  type="button"
                  onClick={() => setOpenIdx(isOpen ? null : idx)}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left md:px-6 md:py-5"
                  aria-expanded={isOpen}
                  aria-controls={`faq-panel-${idx}`}
                >
                  <span className="flex items-start gap-3 min-w-0">
                    <span className="font-mono text-[10px] font-semibold tracking-wider text-stone-400 mt-1 shrink-0">
                      0{idx + 1}
                    </span>
                    <span
                      className={cn(
                        'h-card text-left transition-colors duration-150',
                        isOpen ? 'text-ink' : 'text-stone-800',
                      )}
                    >
                      {item.q}
                    </span>
                  </span>
                  <span
                    className={cn(
                      'flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-all duration-200 ease-out',
                      isOpen
                        ? cn(theme.chipBg, theme.chipIcon, 'rotate-45')
                        : 'bg-stone-100 text-stone-600 group-hover:bg-stone-200',
                    )}
                  >
                    <Plus className="h-3.5 w-3.5" strokeWidth={2.2} />
                  </span>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="panel"
                      id={`faq-panel-${idx}`}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.28, ease: easeStripe }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 pb-5 pl-12 pr-6 text-[14px] leading-relaxed text-stone-600 md:px-6 md:pl-14 md:pb-6">
                        {item.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.article>
            );
          })}
        </div>

        {/* Footer : encore une question ? */}
        <Reveal delay={0.05}>
          <div className="mt-10 flex flex-col items-center gap-3 rounded-2xl border border-dashed border-stone-300 bg-paper/40 px-6 py-6 text-center md:flex-row md:justify-between md:text-left">
            <div className="flex items-center gap-3">
              <span
                className={cn(
                  'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
                  theme.chipBg,
                  theme.chipIcon,
                )}
              >
                <Sparkles className="h-3.5 w-3.5" strokeWidth={1.8} />
              </span>
              <div>
                <p className="h-card">Une question qui n&rsquo;est pas dans la liste&nbsp;?</p>
                <p className="text-[12.5px] text-stone-500">
                  Notre équipe vous répond sous 24h ouvrées · sans engagement.
                </p>
              </div>
            </div>
            <a
              href={`/contact?source=${audience.id}`}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-stone-200 bg-paper px-4 py-2 text-[13px] font-semibold text-ink shadow-[0_1px_2px_rgba(10,10,10,.04)] transition-all duration-150 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-elevated"
            >
              Poser ma question
            </a>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}
